/**
 * "Verify your mobile number" -- send a WhatsApp code to the account mobile,
 * enter it, done.
 *
 * One pop-up, opened from two places (decided with the client, 10 Oct), the
 * same as in the app:
 *
 *   first login - once per account, for customers whose number is not
 *                 verified (MobileVerifyPrompt). The server decides who is due
 *                 and remembers it was shown, so the app does not ask again.
 *   Account     - next to the phone number, for as long as it is not verified.
 *
 * `mobile` is OPTIONAL (changed 18 Oct). It used to be required, and the modal
 * verified only the number already on file. Account mobiles are cleared at
 * go-live -- six numbers were shared across fifteen accounts, so a code sent to
 * one of those would reach a stranger -- which leaves most accounts with
 * nothing to verify. With no number the modal asks for one first and then
 * verifies it; the server writes the proven number back to the account, so
 * entering it here is what restores it.
 *
 * When a number IS given it is shown as before and cannot be edited: that
 * number came from the account, and changing it stays a support request.
 */

import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import NcModal from 'shared/NcModal/NcModal';
import WhatsAppVerifyPanel from 'components/Auth/WhatsAppVerifyPanel';
import { AppDispatch } from 'reduxStore/store';
import { verifyToken } from 'reduxStore/store/auth';

/**
 * The stored mobile as the 10 digits WhatsApp verification works with. Legacy
 * rows hold it in several shapes ("+91 98...", "098...", bare digits); anything
 * that does not come down to a 10-digit Indian mobile cannot be verified.
 */
const tenDigitMobile = (value?: string): string | null => {
    const digits = String(value || '').replace(/\D/g, '');
    const local = digits.length > 10 ? digits.slice(-10) : digits;
    return /^[6-9]\d{9}$/.test(local) ? local : null;
};

/**
 * WhatsApp codes go to Indian numbers only. An account with no code is from
 * before codes were stored, and those are Indian.
 */
export const isIndianDialCode = (code?: string | null): boolean => {
    const digits = String(code ?? '').replace(/\D/g, '');
    return digits === '' || digits === '91';
};

/**
 * The account mobile, when WhatsApp can verify it; otherwise null. A US or
 * Canadian number can look like an Indian mobile (917..., 646...) -- the code
 * is what says which country it is.
 */
export const verifiableAccountMobile = (customer?: { mobile?: string; countryCode?: string | null } | null): string | null =>
    isIndianDialCode(customer?.countryCode) ? tenDigitMobile(customer?.mobile) : null;

export interface MobileVerifyModalProps {
    open: boolean;
    /** The 10-digit account mobile, or undefined when the account has none. */
    mobile?: string | null;
    accessToken?: string;
    /** `true` once the number is verified; `false` for "Not now" or the close button. */
    onClose: (verified: boolean) => void;
}

const MobileVerifyModal: FC<MobileVerifyModalProps> = ({ open, mobile, accessToken, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();

    // The number being verified: the account's, or the one typed below.
    const [entered, setEntered] = useState('');
    const [confirmed, setConfirmed] = useState<string | null>(null);
    const [error, setError] = useState('');

    const accountMobile = mobile || null;
    const target = accountMobile ?? confirmed;

    // Reopening with a different account must not inherit the last answer.
    useEffect(() => {
        if (!open) {
            setEntered('');
            setConfirmed(null);
            setError('');
        }
    }, [open]);

    const useTypedNumber = () => {
        const digits = entered.replace(/\D/g, '');
        if (!/^[6-9]\d{9}$/.test(digits)) {
            setError('Enter a 10 digit Indian mobile number.');
            return;
        }
        setError('');
        setConfirmed(digits);
    };

    const onVerified = () => {
        toast.success('Your number is verified. You can now sign in with WhatsApp.');
        // Re-read the session so `mobileVerified` (and the stored copy the
        // Account page reads) reflect it straight away.
        dispatch(verifyToken());
        onClose(true);
    };

    return (
        <NcModal
            isOpenProp={open}
            onCloseModal={() => onClose(false)}
            renderTrigger={() => null}
            modalTitle="Verify your mobile number"
            contentExtraClass="max-w-md"
            renderContent={() => (
                <div className="space-y-4" data-testid="mobile-verify-modal">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {target
                            ? 'Verify your number once on WhatsApp and you can sign in with a code sent there, as well as by email.'
                            : 'Enter your mobile number. We will send a code on WhatsApp to confirm it is yours.'}
                    </p>

                    {target ? (
                        <WhatsAppVerifyPanel
                            mobile={target}
                            caller={{ accessToken }}
                            context="profile"
                            onVerified={onVerified}
                            // Dismissing a number typed here goes back to the field rather than
                            // closing: it may simply have been mistyped, and the pop-up shows once.
                            onDismiss={() => (accountMobile ? onClose(false) : setConfirmed(null))}
                        />
                    ) : (
                        /* The dial code is fixed at +91 rather than offered as a picker:
                           WhatsApp codes go to Indian numbers only and the server refuses
                           anything else outright, so a picker would offer a dead choice. */
                        <div className="space-y-3" data-testid="mobile-verify-entry">
                            <div className="flex items-stretch gap-2">
                                <span className="flex items-center rounded-2xl bg-neutral-100 px-3 text-sm font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                                    +91
                                </span>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={10}
                                    autoFocus
                                    value={entered}
                                    onChange={(event) => {
                                        setEntered(event.target.value.replace(/\D/g, '').slice(0, 10));
                                        if (error) setError('');
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') useTypedNumber();
                                    }}
                                    placeholder="Mobile number"
                                    aria-label="Mobile number"
                                    data-testid="mobile-verify-input"
                                    className="flex-1 rounded-2xl border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-900"
                                />
                            </div>
                            {error && (
                                <p className="text-sm text-red-600" data-testid="mobile-verify-error">
                                    {error}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={useTypedNumber}
                                data-testid="mobile-verify-continue"
                                className="w-full rounded-2xl bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
                            >
                                Continue
                            </button>
                        </div>
                    )}
                </div>
            )}
        />
    );
};

export default MobileVerifyModal;
